import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Model } from 'survey-core';

import CloseIcon from '@mui/icons-material/Close';

import OnEmailIcon from '../../../icons/OnEmailIcon';
import OnPrintIcon from '../../../icons/OnPrintIcon';
import {
    loadCommunicationTemplate,
    sendJobCentreEmailCommunication
} from '../../../store/communicationService';
import {
    loadDocumentTypes,
    loadFormTypes,
    loadJobCentreDetails
} from '../../../store/directusService';
import { loadParticipantDocumentDetails } from '../../../store/documentService';
import {
    loadForm,
    loadParticipantEmployability,
    printFormParticipant
} from '../../../store/participantService';
import { getNameFromId, getObjFromId } from '../../../utils/directusFunctions';
import {
    DOCUMENT_TYPES_ERROR,
    FORM_ERROR,
    FORM_TYPES_ERROR,
    JOB_CENTRE_DETAILS_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys } from '../../../utils/objectUtils';
import AddDocument from '../../documents/AddDocument';
import SingleSelect from '../../formElements/SingleSelect';
import LoadingSpinner from '../../ui/LoadingSpinner';

import UCD603FormTemplate from './formUCD603/UCD603FormTemplate';
import UCD604FormTemplate from './formUCD604/UCD604FormTemplate';
import FormStatusHistory from './FormStatusHistory';
import { themeJson } from './FormTheme';
import GenerateForm from './GenerateForm';
import GenerateStandardForm from './GenerateStandardForm';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import dropdown from '../../../commonStyles/rowExpand.module.css';
import local from './formStyles/rowDropdownDetails.module.css';

const FormRowDetails = ({ row, participantsAdviser, onClose, isDisabled }) => {
    const dispatch = useDispatch();

    //LOCAL STATE
    const [surveyModel, setSurveyModel] = useState(new Model('{}'));
    const [showDocumentOptions, setShowDocumentOptions] = useState(false);
    const [docType, setDocType] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [documentId, setDocumentId] = useState('');
    const [generatePdf, setGeneratePdf] = useState(false);
    const [generateUCD603Pdf, setGenerateUCD603Pdf] = useState(false);
    const [generateUCD604Pdf, setGenerateUCD604Pdf] = useState(false);
    const [fileName, setFileName] = useState('');
    const [keys, setKeys] = useState({ documentId: '0' });
    const [emailToolTip, setEmailToolTip] = useState({ error: true, msg: '' });
    const [errors, setErrors] = useState({});
    const [isPdfGenerated, setIsPdfGenerated] = useState(false);

    //STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const currentForm = useSelector((state) => state.entities.participantService.currentForm);
    const currentFormParticipant = useSelector(
        (state) => state.entities.participantService.currentFormParticipant
    );
    const currentParticipantEmployability = useSelector(
        (state) => state.entities.participantService.currentParticipantEmployability
    );
    const participantDocuments = useSelector(
        (state) => state.entities.documentService.participantDocumentDetails
    );
    const documentTypes = useSelector((state) => state.entities.directusService.documentTypes);
    const formTypes = useSelector((state) => state.entities.directusService.formTypes);
    const jobCentreDetails = useSelector(
        (state) => state.entities.directusService.jobCentreDetails
    );
    const communicationTemplate = useSelector(
        (state) => state.entities.communicationService.communicationTemplate
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    //USE EFFECTS
    useEffect(() => {
        if (currentParticipantEmployability?.participantId !== currentParticipant.id)
            dispatch(loadParticipantEmployability(currentParticipant.id));
        if (participantDocuments[0]?.participantId !== currentParticipant.id)
            dispatch(loadParticipantDocumentDetails(currentParticipant.id));
        !documentTypes?.length && dispatch(loadDocumentTypes());
        !formTypes?.length && dispatch(loadFormTypes());
        !jobCentreDetails?.length && dispatch(loadJobCentreDetails());
        currentForm?.id !== row.formId && dispatch(loadForm(row.formId));
    }, []);

    useEffect(() => {
        if (!('status' in row) || !('type' in currentForm) || !jobCentreDetails?.length) return;
        let error = true;
        let msg;
        if (row.status !== 'Authorised') msg = 'Unable to create email for participant';
        else if (!currentForm.type) {
            !currentForm.emailTemplateId
                ? (msg =
                      'Form Type is missing or is incompatible and form has no assigned Email Template')
                : (msg = 'Form Type is missing or is incompatible');
        } else if (currentForm.type && !currentForm.emailTemplateId)
            msg = 'Form has no assigned Email Template';
        else if (
            Object.keys(currentParticipantEmployability)?.length < 1 ||
            !currentParticipantEmployability.jobCentreId
        )
            msg = 'Job centre not set for participant';
        else {
            const jobCentre = jobCentreDetails.find(
                (el) => el.id === currentParticipantEmployability.jobCentreId
            );
            if (!jobCentre) msg = "Participant's Job Centre cannot be found";
            else if (currentForm.type === 'Doubt' && !jobCentre.doubt_to_email_address)
                msg = `Job Centre ${jobCentre.name} has no Doubt To Email address`;
            else if (currentForm.type === 'Exit' && !jobCentre.exit_to_email_address)
                msg = `Job Centre ${jobCentre.name} has no Exit To Email address`;
            else {
                error = false;
                msg = 'Create an email for participant';
            }
        }

        setEmailToolTip({ error, msg });
    }, [
        row?.status,
        currentForm?.type,
        currentForm?.emailTemplateId,
        currentParticipantEmployability,
        jobCentreDetails
    ]);

    useEffect(() => {
        if (!participantDocuments) return;
        const newDocuments = participantDocuments.map((el) => ({ ...el, name: el.filename }));
        setDocuments(newDocuments);
    }, [participantDocuments]);

    useEffect(() => {
        const model = new Model(currentForm.data);
        model.applyTheme(themeJson);
        model.showQuestionNumbers = true;
        setSurveyModel(model);
    }, [currentForm?.data]);

    useEffect(() => {
        surveyModel.data = row.data;
    }, [surveyModel.data, row.data]);

    useEffect(() => {
        if (!documentTypes.length) return;
        setDocType(documentTypes.find((el) => el.name === 'Form'));
    }, [documentTypes]);

    useEffect(() => {
        if (
            Object.keys(currentForm)?.length < 1 ||
            Object.keys(communicationTemplate)?.length < 1 ||
            !formTypes?.length ||
            !documentId
        )
            return;

        const payload = {
            participantId: currentParticipant.id,
            subject: communicationTemplate.subject,
            formType: getNameFromId(formTypes, currentForm.typeId),
            body: communicationTemplate.details,
            attachmentId: documentId
        };
        dispatch(
            sendJobCentreEmailCommunication(payload, currentParticipantEmployability.jobCentreId)
        );
    }, [communicationTemplate, formTypes, documentId]);

    useEffect(() => {
        if (successMessage === `Form participant has been printed successfully`) {
            if (currentForm.name === currentFormParticipant?.name) {
                if (currentForm.name.includes('UCD604')) setGenerateUCD604Pdf(true);
                else if (currentForm.name.includes('603')) setGenerateUCD603Pdf(true);
                else setGeneratePdf(true);
                if (showDocumentOptions) setShowDocumentOptions(false);
            }
        }
    }, [successMessage, currentFormParticipant?.name]);

    // FNS
    const loadingError = () => {
        if (!documentTypes?.length) return DOCUMENT_TYPES_ERROR;
        if (!formTypes?.length) return FORM_TYPES_ERROR;
        if (!jobCentreDetails?.length) return JOB_CENTRE_DETAILS_ERROR;
        if (Object.keys(currentForm)?.length < 1) return FORM_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((errors) => ({ ...errors, [key]: { error: false, message: '' } }));
    };

    //EVENT HANDLERS
    const onPrintForm = () => {
        setFileName(`${currentForm.name}_${format(new Date(), `dd_MMM_yyyy_HH_mm`)}`);
        dispatch(printFormParticipant(row.id));
    };

    const onDocumentChange = (chosenId) => {
        clearError('documentId');
        setDocumentId(chosenId);
        currentForm?.emailTemplateId &&
            dispatch(loadCommunicationTemplate(currentForm.emailTemplateId));
        setShowDocumentOptions(false);
    };

    const onPdfGenerated = (isGenerated) => {
        setIsPdfGenerated(isGenerated);
    };

    const onExit = () => {
        isPdfGenerated && setIsPdfGenerated(false);
        setKeys(clearKeys(keys));
        setErrors({});
        onClose();
    };

    // RENDER
    const errorMsg = loadingError();
    if (errorMsg) {
        return <LoadingSpinner content={errorMsg} />;
    }

    return (
        <div className={`${dropdown.rowDropdown} ${dropdown.redBorder}`} data-testid="div_start">
            <div className={local.actionRows}>
                <div className={local.actionRow}>
                    <span className={local.label}>Print Form</span>
                    <OnPrintIcon
                        onPrint={onPrintForm}
                        active={!isDisabled && row.status === 'Authorised'}
                        tooltip={
                            row.status === 'Authorised'
                                ? 'Create a PDF for participant'
                                : 'Unable to print email for participant'
                        }
                    />
                </div>

                <div className={local.actionRow}>
                    <span className={local.label}>Email Form</span>
                    <OnEmailIcon
                        onEmail={() => setShowDocumentOptions(true)}
                        active={!isDisabled && !emailToolTip.error}
                        tooltip={emailToolTip.msg}
                    />
                </div>
            </div>

            <div className={local.history}>
                <h2 className={app.sectionHeading}>Form History</h2>
                {row.history.map((el) => (
                    <FormStatusHistory key={el.date} history={el} />
                ))}
            </div>

            {generatePdf && (
                <GenerateStandardForm
                    surveyModel={surveyModel}
                    form={currentForm}
                    row={row}
                    currentParticipant={currentParticipant}
                    participantsAdviser={participantsAdviser}
                    onPdfGenerated={onPdfGenerated}
                    onClose={() => setGeneratePdf(false)}
                />
            )}
            {generateUCD604Pdf && (
                <GenerateForm
                    fileName={fileName}
                    formCode={currentForm.code}
                    ptCode={currentParticipant.ptCode}
                    userCode={loggedInUser?.code || ''}
                    onPdfGenerated={onPdfGenerated}
                    onClose={() => setGenerateUCD604Pdf(false)}>
                    <UCD604FormTemplate surveyAnswers={currentFormParticipant.data} />
                </GenerateForm>
            )}
            {generateUCD603Pdf && (
                <GenerateForm
                    fileName={fileName}
                    formCode={currentForm.code}
                    ptCode={currentParticipant.ptCode}
                    userCode={loggedInUser?.code || ''}
                    onPdfGenerated={onPdfGenerated}
                    onClose={() => setGenerateUCD603Pdf(false)}>
                    <UCD603FormTemplate surveyAnswers={currentFormParticipant.data} />
                </GenerateForm>
            )}

            {isPdfGenerated && (
                <>
                    <div className={form.successMessage}>PDF {currentForm.name} downloaded</div>
                    <AddDocument
                        onClose={onExit}
                        onCancel={onExit}
                        isDisabled={isDisabled}
                        btnText="Close"
                        currentDocumentType={docType ? docType : null}
                        btnDisabled={true}
                    />
                </>
            )}
            {showDocumentOptions && (
                <SingleSelect
                    id="documentId"
                    key={keys.documentId}
                    label="Please select a document to email"
                    placeholder="Select a document..."
                    disabled={isDisabled}
                    mandatory={true}
                    menuItems={documents || []}
                    selectedId={documentId}
                    selected={getObjFromId(documents, documentId)}
                    error={errors.documentId}
                    onChange={onDocumentChange}
                />
            )}

            <div className={dropdown.exit} onClick={onExit}>
                Close details
                <CloseIcon />
            </div>
        </div>
    );
};

FormRowDetails.propTypes = {
    row: PropTypes.object.isRequired,
    participantsAdviser: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool.isRequired
};

export default FormRowDetails;
